import { useMemo } from 'react'

import T from 'T'

import type { HHFormSelectOption } from 'components/form-fields/v5/HHFormSelectField'
import type { AccountLifecycleCompetitorApi, AccountLifecycleReasonApi } from 'types/api/account-lifecycle/accountLifecycle'

type LifecycleReasonOptionsArgs = {
  reasons: AccountLifecycleReasonApi[]
  competitors: AccountLifecycleCompetitorApi[]
  isReasonRequired: boolean
  selectedReasonId: string | undefined
}

// Neither list endpoint imposes an order, so the picker sorts what it shows.
const byName = <Named extends { name: string }>(items: Named[]) => [...items].sort((a, b) => a.name.localeCompare(b.name, 'en-US'))

/**
 * Options for a won or loss reason select and its competitor select, and whether the competitor applies.
 * An optional reason starts with None so it can be unset; a required one offers no blank.
 */
const useLifecycleReasonOptions = ({ reasons, competitors, isReasonRequired, selectedReasonId }: LifecycleReasonOptionsArgs) => {
  const reasonOptions: HHFormSelectOption[] = useMemo(() => {
    const options = byName(reasons).map(reason => ({ key: reason.id, value: reason.id, label: reason.name }))
    return isReasonRequired ? options : [{ key: 'no-reason', value: '', label: T.NONE }, ...options]
  }, [reasons, isReasonRequired])
  const competitorOptions: HHFormSelectOption[] = useMemo(
    () => byName(competitors).map(competitor => ({ key: competitor.id, value: competitor.id, label: competitor.name })),
    [competitors]
  )
  const showCompetitor = useMemo(
    () => reasons.some(reason => reason.id === selectedReasonId && reason.requiresCompetitor),
    [reasons, selectedReasonId]
  )
  return { reasonOptions, competitorOptions, showCompetitor }
}

export default useLifecycleReasonOptions
